import { template } from "@ember/template-compiler";
import { hash } from '@ember/helper';
import { Compiled } from '../services/kolay/compiler/reactive.ts';
import { Load } from './utils.gts';
import type { TOC } from '@ember/component/template-only';
import type { ArrayType, DeclarationReference, DeclarationReflection, LiteralType, NamedTupleMember, ReferenceType, SignatureReflection, SomeType, TupleType, UnionType, UnknownType } from 'typedoc';
/**
 * Assumptions:
 * - we are documenting public API
 *   - component properties and methods are not public API
 *     - including the constructor, inherited methods, etc
 *   - only the signature describes what the public API is.
 */ export const APIDocs: TOC<{
    Args: {
        /**
     * Which module to import the type from
     */ module: string;
        /**
     * The name of the export to render the type / JSDoc of
     */ name: string;
        /**
     * The name of the package to lookup the module and export name.
     */ package: string;
    };
}> = template(`
  <Load @module={{@module}} @name='{{@name}}' @package={{@package}} as |info|>
    <Declaration @info={{info}} />
  </Load>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
/**
 * Used for referencing the comment on a const or class.
 *
 * For example:
 * ```
 * /*
 *  * Comment block here is what is targeted
 *  *\/
 * export const CommentQuery ...
 * ```
 *
 * Usage:
 * ```hbs
 * <CommentQuery @name="CommentQuery" ... />
 * ```
 */ export const CommentQuery: TOC<{
    Args: {
        /**
     * Which module to import the type from
     */ module: string;
        /**
     * The name of the export to render the type / JSDoc of
     */ name: string;
        /**
     * The name of the package to lookup the module and export name.
     */ package: string;
    };
}> = template(`
  <Load @package={{@package}} @module={{@module}} @name={{@name}} as |info|>
    <Comment @info={{info}} />
  </Load>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const join = (lines1: string[])=>lines1.join('\n');
const text = (lines1: {
    text: string;
}[])=>lines1.map((line1)=>line1.text);
export function isGlimmerComponent(info1: DeclarationReference) {
    let extended1 = (info1 as any)?.extendedTypes?.[0];
    if (!extended1) return false;
    return (extended1.name === 'default' && extended1.package === '@glimmer/component');
}
export const Comment: TOC<{
    Args: {
        info: {
            comment?: {
                summary?: {
                    text: string;
                }[];
            };
        };
    };
}> = template(`
  {{#if @info.comment.summary}}
    {{#let (Compiled (join (text @info.comment.summary))) as |compiled|}}
      {{#if compiled.isReady}}
        <div class='typedoc-rendered-comment'>
          <compiled.component />
        </div>
      {{/if}}
    {{/let}}
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const isIgnored = (name1: string)=>[
        '__type',
        'TOC',
        'TemplateOnlyComponent'
    ].includes(name1);
const isConst = (x1: {
    flags: {
        isConst: boolean;
    };
})=>x1.flags.isConst;
const not = (x1: unknown)=>!x1;
const Declaration: TOC<{
    Args: {
        info: DeclarationReflection | undefined;
    };
}> = template(`
  {{#if @info}}
    <div class='typedoc-declaration'>
      {{#if (not (isIgnored @info.name))}}
        <span class='typedoc-declaration-name'>{{@info.name}}</span>
      {{/if}}

      {{#if (isConst @info)}}
        <Comment @info={{@info}} />
      {{/if}}

      {{#if @info.type}}
        <Type @info={{@info.type}} />
      {{/if}}

      {{#if @info.children}}
        <ul class='typedoc-declaration-children'>
          {{#each @info.children as |child|}}
            <li><Declaration @info={{child}} /></li>
          {{/each}}
        </ul>
      {{/if}}

      {{#if @info.signatures}}
        <ul class='typedoc-declaration-signatures'>
          {{#each @info.signatures as |child|}}
            {{! @glint-expect-error }}
            <li><Type @info={{child}} /></li>
          {{/each}}
        </ul>
      {{/if}}

      {{#if (not (isConst @info))}}
        {{#if @info.comment.summary}}
          <Comment @info={{@info}} />
        {{/if}}
      {{/if}}
    </div>
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Reflection: TOC<{
    info: {
        declaration: DeclarationReflection;
    };
}> = template(`<Declaration @info={{@info.declaration}} />`, {
    eval () {
        return eval(arguments[0]);
    }
});
const isReference = (x1: {
    type: string;
})=>x1.type === 'reference';
const isReflection = (x1: {
    type: string;
})=>x1.type === 'reflection';
export const isIntrinsic = (x1: {
    type: string;
})=>x1.type === 'intrinsic';
const isTuple = (x1: {
    type: string;
})=>x1.type === 'tuple';
const isNamedTuple = (x1: SomeType | undefined): x is NamedTupleMember =>x1?.type === 'namedTupleMember';
const isVoidIntrinsic = (x1: unknown | undefined)=>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('type' in x1)) return false;
    if (typeof x1.type === 'object' && x1.type !== null) {
        if ('type' in x1.type && 'name' in x1.type) {
            return x1.type.type === 'intrinsic' && x1.type.name === 'void';
        }
    }
    return false;
};
const isArray = (x1: SomeType | undefined): x is ArrayType =>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('type' in x1)) return false;
    return x1.type === 'array';
};
const isFn = (x1: SomeType | undefined)=>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('name' in x1)) return false;
    if (!('variant' in x1)) return false;
    return x1.variant === 'signature';
};
const isUnknownType = (x1: SomeType | undefined): x is UnknownType =>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('type' in x1)) return false;
    return x1.type === 'unknown';
};
const isUnion = (x1: SomeType | undefined): x is UnionType =>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('type' in x1)) return false;
    return x1.type === 'union';
};
const isLiteral = (x1: SomeType | undefined): x is UnionType =>{
    if (!x1) return false;
    if (typeof x1 !== 'object') return false;
    if (x1 === null) return false;
    if (!('type' in x1)) return false;
    return x1.type === 'literal';
};
// function typeArg(info: DeclarationReference) {
//   let extended = info?.extendedTypes?.[0]
//   if (!extended) return false;
//   return extended.typeArguments[0]
// }
//
const isInvokable = (info1: ReferenceType)=>info1.name === 'Invokable';
const Reference: TOC<{
    info: ReferenceType;
}> = template(`
  {{#if (isInvokable @info)}}
    <div class='typedoc__unknown__yield'>
      <Intrinsic @info={{hash name='Component'}} />
    </div>
  {{else}}
    <div class='typedoc__reference'>
      {{#if (not (isIgnored @info.name))}}
        <div class='typedoc__reference__name'>{{@info.name}}</div>
      {{/if}}
      {{#if @info.typeArguments.length}}
        <div class='typedoc__reference__typeArguments'>
          &lt;
          {{#each @info.typeArguments as |typeArg|}}
            <div class='typedoc__reference__typeArgument'>
              <Type @info={{typeArg}} />
            </div>
          {{/each}}
          &gt;
        </div>
      {{/if}}
    </div>
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Intrinsic: TOC<{
    info: {
        name: string;
    };
}> = template(`
  <span class='typedoc-intrinsic'>{{@info.name}}</span>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const VoidIntrinsic: TOC<{
    info: {
        name: string;
    };
}> = template(`
  <div class='typedoc__void_intrinsic'>
    {{! @glint-expect-error }}
    <Function @info={{@info}} />
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Tuple: TOC<{
    Args: {
        info: TupleType;
    };
}> = template(`
  {{#each @info.elements as |element|}}
    <Type @info={{element}} />
  {{/each}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const NamedTuple: TOC<{
    Args: {
        info: NamedTupleMember;
    };
}> = template(`
  <div class='typedoc-named-tuple'>
    <div class='typedoc-name'>{{@info.name}}</div>
    <Type @info={{@info.element}} />
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Array: TOC<{
    Args: {
        info: ArrayType;
    };
}> = template(`
  <div class='typedoc__array'>
    <div class='typedoc__array__indicator'>Array of</div>
    <Type @info={{@info.elementType}} />
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Function: TOC<{
    Args: {
        info: SignatureReflection;
    };
}> = template(`
  <div class='typedoc__function'>
    <div class='typedoc__function_comment'>
      <Comment @info={{@info}} />
    </div>
    <div class='typedoc__function__type'>
      <div class='typedoc__function__open'>(</div>
      <div class='typedoc__function__parameters'>
        {{#each @info.parameters as |param|}}
          <div class='typedoc__function__parameter__container'>
            <div class='typedoc__function__parameter'>
              <div
                class='typedoc__function__parameter__name'
              >{{param.name}}</div>
              <div class='typedoc__function__parameter__type'>
                {{! @glint-expect-error }}
                <Type @info={{param.type}} />
              </div>
            </div>
            <div class='typedoc__function__parameter__comment'>
              <Comment @info={{param}} />
            </div>
          </div>
        {{/each}}
      </div>
      <div class='typedoc__function__close'>) =></div>
      <div class='typedoc__function__return_type'>
        {{! @glint-expect-error }}
        <Type @info={{@info.type}} />
      </div>
    </div>
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Unknown: TOC<{
    Args: {
        info: any;
    };
}> = template(`
  <div class='typedoc__unknown'>
    {{@info.name}}
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const Union: TOC<{
    Args: {
        info: UnionType;
    };
}> = template(`
  <div class='typedoc__union'>
    {{#each @info.types as |type|}}
      <div class='typedoc__union__type'>
        <Type @info={{type}} />
      </div>
    {{/each}}
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
const literalAsString = (x1: LiteralType['value'])=>{
    if (typeof x1 === 'string') {
        return `"${x1}"`;
    }
    if (typeof x1 === 'number' || typeof x1 === 'boolean' || x1 === null) {
        return `${x1}`;
    }
    return x1.toString();
};
const Literal: TOC<{
    Args: {
        info: LiteralType;
    };
}> = template(`
  <div class='typedoc__literal'>
    {{literalAsString @info.value}}
  </div>
`, {
    eval () {
        return eval(arguments[0]);
    }
});
export const Type: TOC<{
    Args: {
        info: SomeType;
    };
}> = template(`
  {{#if (isReference @info)}}
    {{! @glint-expect-error }}
    <Reference @info={{@info}} />
  {{else if (isReflection @info)}}
    {{! @glint-expect-error }}
    <Reflection @info={{@info}} />
  {{else if (isIntrinsic @info)}}
    {{! @glint-expect-error }}
    <Intrinsic @info={{@info}} />
  {{else if (isTuple @info)}}
    {{! @glint-expect-error }}
    <Tuple @info={{@info}} />
  {{else if (isNamedTuple @info)}}
    <NamedTuple @info={{@info}} />
  {{else if (isVoidIntrinsic @info)}}
    {{! @glint-expect-error }}
    <VoidIntrinsic @info={{@info}} />
  {{else if (isArray @info)}}
    <Array @info={{@info}} />
  {{else if (isFn @info)}}
    {{! @glint-expect-error }}
    <Function @info={{@info}} />
  {{else if (isUnion @info)}}
    <Union @info={{@info}} />
  {{else if (isLiteral @info)}}
    <Literal @info={{@info}} />
  {{else if (isUnknownType @info)}}
    <Unknown @info={{@info}} />
  {{else}}
    {{! template-lint-disable no-log }}
    {{log 'Unknown Type' @info}}
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
//# sourceMappingURL=data:application/json;base64,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